import React, { useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from "react";
import { Stage, Layer, Rect, Text, Arrow, Group, Image } from "react-konva";
import { BezierLink } from "./BezierLink";
import { AutoSizer } from "./AutoSizer";
import { Checkbox } from "@heroui/react";
import useDataEvent from "./CustomHook";
import { convertIconForKonva, renderReactSVGIcon } from "./RenderHelpers";
import { HeroIcon } from "./HeroIcon";
import { StaticImage } from "./StaticImage";
import Konva from "konva";
import AnimatedKonvaProgressBar from "./AnimatedKonvaProgressBar";


import dagre from "dagre";
import PrimitiveConfig from "./PrimitiveConfig";
import { useRunningAnimation } from "./@components/RunningAnimation";


/**
 * DependencyTreeKonva
 *
 * Props:
 *   - statusMap: an object where each key is a node ID and its value is an object containing
 *                at least a `children` array of child-status objects (each with an `id` field).
 *   - width: desired canvas width (default: 1000)
 *   - height: desired canvas height (default: 800)
 *
 * Example statusMap shape:
 * {
 *   "stepA": { id: "stepA", children: [ { id: "stepB" }, { id: "stepC" } ], … },
 *   "stepB": { id: "stepB", children: [ { id: "stepD" } ], … },
 *   "stepC": { id: "stepC", children: [ ], … },
 *   "stepD": { id: "stepD", children: [ ], … },
 *   // …
 * }
 */

const NODE_WIDTH = 100;
const NODE_HEIGHT = 60;

const colorMap = {
  "not_run": {base: "#f2f2f2", highlight: "#333"},
  "complete": {base: "#ecfdf5", highlight: "#34d399"},
  "waiting": {base: "#fef08a", highlight: "#f59e0b"},
  "running": {base: "#e0f2fe", highlight: "#0ea5e9"},
  "error": {base: "#fee2e2", highlight: "#ef4444"},
  "rerun": {base: "#fee2e2", highlight: "#ef4444"},
  "error_skip": {base: "#fbbf24", highlight: "#fef9c3"},
  "flow_control": {base: "#ede9fe", highlight: "#7c3aed"},
  "flow_control_pending": {base: "#f3f4f6", highlight: "#6b7280"},
  "flow_control_halt": {base: "#fee2e2", highlight: "#dc2626"}
}

export default function WorkflowStructurePreview({ statusMap, ...props }) {
  // 1) Build `nodes` and `edges` arrays from statusMap
  const [showHidden, setShowHidden] = useState(false)
  const [showSkipped, setShowSkipped] = useState(false)
  const [groupByLabels, setGroupByLabels] = useState(true)

  const stageRef = useRef()
  const layerRef = useRef()
  const offsetRef = useRef(0);
  const [update, setUpdate] = useState({})
  const [ready, setReady] = useState(false)


  const { nodes, edges, visibleIds } = useMemo(() => {
    const {nodes, edges, visibleIds} = PrimitiveConfig.flowInstanceStatusToMap( statusMap, {showHidden, showSkipped, groupByLabels})
    const sizedNodes = nodes.map(node=>{
      let width = NODE_WIDTH;
      let height = NODE_HEIGHT;
      if(node.primitiveType === "flowcontrol"){
          const runtime = node.flowControl ?? {};
          const statesCount = node.flowControlStates?.length ?? 0;
          let extraLines = statesCount;
          if(runtime.pendingSources?.length){
            extraLines += 1;
          }
          if(runtime.haltFlow){
            extraLines += 1;
          }
          width = 180;
          height = Math.max(NODE_HEIGHT + 20, 70 + extraLines * 14);
      }
      return {...node, width, height};
    })
    return {nodes: sizedNodes, edges, visibleIds}
      
  }, [statusMap, showHidden, showSkipped, groupByLabels]);

  const nodeLookup = useMemo(()=>{
    return nodes.reduce((acc,node)=>{
      acc[node.id] = node;
      return acc;
    }, {})
  }, [nodes]);

  function doUpdate(thisUpdate){
    setUpdate(prev => {
      const next = { ...prev };
      for (const key of Object.keys(thisUpdate)) {
        next[key] = (next[key] ?? 0) + 1;
      }
      return next;
    });
  }
  
  useDataEvent(["set_parameter","set_field"], visibleIds, (ids, event, info, remote, items)=>{
    if( info.startsWith("processing.flow")){
      const newState = {}
      for(const p of items){
       newState[p.id] = true
      }
     doUpdate(newState)
    }
  })

  // 2) Compute x/y positions for each node using dagre tree layout
  const positions = useMemo(() => {
    if (!nodes.length) return {};
    const pos = computeTreeLayout(nodes, edges);
    let maxX = 0, maxY = 0;
    nodes.forEach(node => {
      const p = pos[node.id] ?? { x: 0, y: 0 };
      if (p.x + (node.width ?? NODE_WIDTH) > maxX) maxX = p.x + (node.width ?? NODE_WIDTH);
      if (p.y + (node.height ?? NODE_HEIGHT) > maxY) maxY = p.y + (node.height ?? NODE_HEIGHT);
    });
    setReady({ maxX, maxY });
    return pos;
  }, [nodes, edges]);

  useRunningAnimation(layerRef, offsetRef);

  if(!ready){
    return <></>
  }

  return (
    <AutoSizer enableResizeObserver={true}>
        {({ width, height }) => {
            const scale = Math.min(1, width / ready.maxX * 0.95, height / ready.maxY * 0.95 ) 
            const ox = ((width - (ready.maxX * scale)) / 2) / scale
            const oy = ((height - (ready.maxY * scale)) / 2) / scale
            let anyRunning = false
            return <>
              <Stage ref={stageRef} width={width} height={height} scaleX={scale} scaleY={scale} offsetX={-ox} offsetY={-oy}>
                <Layer ref={layerRef}>
                  {/* 4a) Draw edges as arrows */}
                  {/* Draw edges using getLinkPoints */}
                  {edges.map(({ from, to }, idx) => {
                    const targetNode = nodeLookup[to];
                    let running = targetNode && targetNode.primitiveType !== "flowcontrol" ? targetNode.status() === "running" : false
                    anyRunning ||= running
                    return (
                      <BezierLink
                      key={`arrow-${idx}`}
                      points={getLinkPoints(from, to, positions, nodeLookup)}
                      stroke="#76a3ff"
                      dashEnabled={running}
                      dash={[6,4]}
                      dashOffset={0}
                      name={running ? "running" : ""}
                      strokeWidth={1}
                      pointerLength={6}
                      pointerWidth={6}
                    />
                    );
                  })}
                  {/* 4b) Draw nodes as groups of Rect + Text */}
                  {nodes.map((node) => {
                    const { id, name, width: nodeW, height: nodeH, skipped, status, progress, icon, candidateForRun, flowControl, flowControlStates, primitiveType } = node;
                    const statusValue = status()
                    const progressValue = progress()
                    const { x, y } = positions[id];
                    const padding = [nodeH * 0.1, nodeW * 0.05]
                    const isFlowControl = primitiveType === "flowcontrol";
                    let displayStatus = statusValue
                    if(isFlowControl){
                      if(flowControl?.haltFlow){
                        displayStatus = "flow_control_halt"
                      }else if(flowControl?.evaluated){
                        displayStatus = "flow_control"
                      }else{
                        displayStatus = "flow_control_pending"
                      }
                    }
                    const isRunning = displayStatus === 'running';
                    let renderedIcon
                    let hasIcon = false
                    if( icon ){
                      renderedIcon = convertIconForKonva( <HeroIcon icon={icon} className="w-5 h-5"/>, {width: 28, height: 28}).icon
                      hasIcon = true
                    }
                    //const nodeColor = (!anyRunning && candidateForRun) ? {base: "#f3e8ff", highlight: "#c084fc"} : (colorMap[status] ?? {base: "#f2f2f2", highlight:"#333"})
                    const nodeColor = (colorMap[displayStatus] ?? colorMap[statusValue] ?? {base: "#f2f2f2", highlight:"#333"})
                    const runtime = flowControl ?? {}
                    const flowStates = Array.isArray(flowControlStates) ? flowControlStates : []
                    const stateLines = []
                    if(isFlowControl){
                      const resultMap = runtime.stateResults ?? {}
                      const pending = !runtime.evaluated || (runtime.pendingSources?.length ?? 0) > 0
                      const matchedSet = new Set(runtime.matchedStates ?? [])
                      flowStates.forEach((state, idx)=>{
                        const key = state.id ?? state.pinKey ?? `state-${idx}`
                        const result = resultMap[state.id] ?? resultMap[state.pinKey] ?? resultMap[key]
                        const passed = result?.passed === true
                        const indicator = pending && !passed ? "…" : passed ? "✔" : "✖"
                        const halt = state.haltFlow === true
                        let fill = "#9ca3af"
                        if(pending && !passed){
                          fill = "#6b7280"
                        }else if(passed){
                          fill = halt ? "#b91c1c" : "#16a34a"
                        }
                        stateLines.push({
                          key: `state-${idx}`,
                          text: `${indicator} ${state.name ?? state.pinKey ?? `State ${idx + 1}`}${halt ? " (halt)" : ""}`,
                          fill
                        })
                      })
                      if((runtime.pendingSources?.length ?? 0) > 0){
                        stateLines.push({
                          key: "pending",
                          text: `Pending inputs: ${runtime.pendingSources.length}`,
                          fill: "#6b7280"
                        })
                      }
                      if(!pending && flowStates.length > 0 && !stateLines.some(line=>line.fill === "#16a34a" || line.fill === "#b91c1c")){
                        stateLines.push({
                          key: "no-match",
                          text: "No matches",
                          fill: "#6b7280"
                        })
                      }
                      if(runtime.haltFlow && !stateLines.some(line=>line.key === "halt")){
                        stateLines.push({
                          key: "halt",
                          text: "Flow halted",
                          fill: "#b91c1c"
                        })
                      }
                    }
                    return (
                      <Group
                        key={id}
                        x={x}
                        y={y}
                        update={update[id] ?? 0}
                        id={id}
                        opacity={skipped ? 0.5 : undefined}
                        onClick={() =>{
                          if(props.onClick){
                            props.onClick(node.itemIds, node)
                          }
                        }}
                        onMouseEnter={(e) => {
                          const container = e.currentTarget.getStage().container();
                          container.style.cursor = "pointer";
                        }}
                        onMouseLeave={(e) => {
                          const container = e.currentTarget.getStage().container();
                          container.style.cursor = "default";
                        }}
                      >
                        {isRunning && <>
                                      <Rect 
                                        width={nodeW + 30}
                                        height={nodeH + (progressValue ? 46 : 30)}
                                        x={-15}
                                        y={-15}
                                        cornerRadius={20}
                                        dashEnabled={true}
                                        name="running"
                                        dash={[6, 4]}
                                        dashOffset={0}
                                        strokeWidth={1.5}
                                        stroke={nodeColor.highlight}
                                        />
                                        {progressValue && <AnimatedKonvaProgressBar x={0} y={nodeH+10} progress={progressValue} width={nodeW} height={12} stripeColor={nodeColor.highlight} backgroundColor={nodeColor.base}/>}
                        </>}
                        <Rect
                          width={nodeW}
                          height={nodeH}
                          //fill={nodeColor}
                          dashEnabled={skipped}
                          dash={skipped ? [5,4] : undefined}
                          strokeScaleEnabled={false}
                          cornerRadius={4}
                          fillLinearGradientStartPoint={{ x: nodeW / 3, y: 0 }}
                          fillLinearGradientEndPoint={{ x: nodeW / 2, y: nodeH }}
                          fillLinearGradientColorStops={[
                                0, 'white',   // light blue
                                //0.5, '#d1c4e9', // soft lavender
                                0.9, nodeColor.base,
                          ]}
                          // Outline
                          //stroke='#b7d8ff'
                          stroke={nodeColor.highlight}
                          strokeWidth= {1}
                          shadowColor = '#000000'
                          shadowBlur = {6}
                          shadowOffset ={{ x: 2, y: 2 }}
                          shadowOpacity = {0.08}
                        />
                        {renderedIcon && <StaticImage x={(nodeW - 28) / 2} y={padding[0] / 2} color={nodeColor.highlight} svgString={renderedIcon} width={28} height={28}/>}
                        {(() => {
                          const elements = []
                          const titleY = padding[0] + (hasIcon ? 28 : 0)
                          elements.push(
                            <Text
                              key={`${id}-title`}
                              text={name}
                              fontSize={11}
                              fontStyle="bold"
                              fill="#24292e"
                              x={padding[1]}
                              y={titleY}
                              width={nodeW - (padding[1] * 2)}
                              align="center"
                            />
                          )
                          if(isFlowControl && stateLines.length > 0){
                            let currentY = titleY + 14
                            stateLines.forEach((line, idx)=>{
                              elements.push(
                                <Text
                                  key={`${id}-${line.key}`}
                                  text={line.text}
                                  fontSize={9}
                                  fill={line.fill}
                                  x={padding[1]}
                                  y={currentY}
                                  width={nodeW - (padding[1] * 2)}
                                  align="left"
                                />
                              )
                              currentY += 12
                            })
                          }
                          return elements
                        })()}
                      </Group>
                    );
                  })}
                </Layer>
              </Stage>
              <div className="absolute right-2 top-2 bg-white border shadow-lg rounded-lg px-2 pt-1 flex flex-col space-y-1 ">
                <Checkbox size="sm" isSelected={showSkipped} onValueChange={setShowSkipped}>Show skipped</Checkbox>
                <Checkbox size="sm" isSelected={showHidden} onValueChange={setShowHidden}>Show hidden</Checkbox>
                <Checkbox size="sm" isSelected={groupByLabels} onValueChange={setGroupByLabels}>Group by labels</Checkbox>
              </div>
            </>
        }}
      </AutoSizer>
  );
}
// ── Rectangle overlap test ────────────────────────────────────────────────────
function rectsOverlap(a, b, gap = 0) {
  return !(
    a.x + a.width + gap <= b.x ||
    b.x + b.width + gap <= a.x ||
    a.y + a.height + gap <= b.y ||
    b.y + b.height + gap <= a.y
  );
}

// ── Overlap resolution: detect and push apart nodes that overlap ──────────────
function resolveOverlaps(positions, nodes, passes = 8) {
  const nodeList = nodes.map(n => {
    const pos = positions[n.id] ?? { x: 0, y: 0 };
    return {
      id: n.id,
      x: pos.x,
      y: pos.y,
      width: n.width ?? NODE_WIDTH,
      height: n.height ?? NODE_HEIGHT,
    };
  });

  // Spatial cell hashing to avoid O(n²) for large graphs
  const CELL = Math.max(80, Math.max(...nodeList.map(n => n.height)) * 1.5);

  for (let pass = 0; pass < passes; pass++) {
    const grid = new Map();
    nodeList.forEach(n => {
      const cMin = Math.floor(n.x / CELL);
      const cMax = Math.floor((n.x + n.width) / CELL);
      const rMin = Math.floor(n.y / CELL);
      const rMax = Math.floor((n.y + n.height) / CELL);
      for (let r = rMin; r <= rMax; r++) {
        for (let c = cMin; c <= cMax; c++) {
          const key = `${r},${c}`;
          if (!grid.has(key)) grid.set(key, []);
          grid.get(key).push(n);
        }
      }
    });

    let changed = false;
    const checked = new Set();
    grid.forEach((cellRects) => {
      for (let i = 0; i < cellRects.length; i++) {
        for (let j = i + 1; j < cellRects.length; j++) {
          const a = cellRects[i];
          const b = cellRects[j];
          if (a.id === b.id) continue;
          const pairKey = a.id < b.id ? `${a.id}:${b.id}` : `${b.id}:${a.id}`;
          if (checked.has(pairKey)) continue;
          checked.add(pairKey);
          if (!rectsOverlap(a, b, 6)) continue;

          // Push the node that is further right downward; push shallower down if same x
          if (a.x + a.width / 2 <= b.x + b.width / 2) {
            const push = a.y + a.height + 8 - b.y;
            if (push > 0) {
              b.y += push;
              positions[b.id] = { ...positions[b.id], y: b.y };
              changed = true;
            }
          } else {
            const push = b.y + b.height + 8 - a.y;
            if (push > 0) {
              a.y += push;
              positions[a.id] = { ...positions[a.id], y: a.y };
              changed = true;
            }
          }
        }
      }
    });
    if (!changed) break;
  }
}

// ── Synchronous dagre-based tree layout with dynamic spacing ──────────────────
function computeTreeLayout(nodes, edges) {
  const positions = {};
  nodes.forEach(n => { positions[n.id] = { x: 0, y: 0 }; });
  if (!nodes.length) return positions;

  const g = new dagre.graphlib.Graph();

  // Dynamic spacing that scales with actual node sizes and count
  const heights = nodes.map(n => n.height ?? NODE_HEIGHT);
  const widths = nodes.map(n => n.width ?? NODE_WIDTH);
  const maxH = Math.max(NODE_HEIGHT, ...heights);
  const maxW = Math.max(NODE_WIDTH, ...widths);

  // More generous spacing for denser graphs to prevent overlap
  const densityFactor = Math.min(2, Math.max(1, nodes.length / 12));
  const nodesep = Math.ceil(maxH * 0.85 * densityFactor);
  const ranksep = Math.ceil(maxW * 1.5 * densityFactor);

  g.setGraph({
    rankdir: "LR",
    nodesep,
    ranksep,
    ranker: "network-simplex",
  });
  g.setDefaultEdgeLabel(() => ({}));

  nodes.forEach(node => {
    g.setNode(node.id, {
      width: node.width ?? NODE_WIDTH,
      height: node.height ?? NODE_HEIGHT,
    });
  });

  edges.forEach(edge => {
    g.setEdge(edge.from, edge.to);
  });

  dagre.layout(g);

  // Convert dagre centre positions to top-left
  nodes.forEach(node => {
    const dn = g.node(node.id);
    if (dn) {
      positions[node.id] = {
        x: dn.x - ((dn.width ?? node.width ?? NODE_WIDTH) / 2),
        y: dn.y - ((dn.height ?? node.height ?? NODE_HEIGHT) / 2),
      };
    }
  });

  // Safety net: resolve any remaining overlaps
  resolveOverlaps(positions, nodes);

  return positions;
}



function getLinkPoints(fromId, toId, positions, nodeLookup) {
  const fromNode = nodeLookup[fromId] ?? {};
  const toNode = nodeLookup[toId] ?? {};
  const { x: px = 0, y: py = 0 } = positions[fromId] ?? {x:0,y:0};
  const { x: cx = 0, y: cy = 0 } = positions[toId] ?? {x:0,y:0};
  const fromWidth = fromNode.width ?? NODE_WIDTH;
  const fromHeight = fromNode.height ?? NODE_HEIGHT;
  const toHeight = toNode.height ?? NODE_HEIGHT;

  // 1) Anchor the start on the right‐center of the parent box:
  const startX = px + fromWidth;
  const startY = py + fromHeight / 2;

  // 2) Anchor the end on the left‐center of the child box:
  const endX = cx;
  const endY = cy + toHeight / 2;

  const dx = endX - startX;

  const cp1X = startX + dx * 0.85;
  const cp1Y = startY;

  const cp2X = startX + dx * 0.15;
  const cp2Y = endY;

  return [startX, startY, cp1X, cp1Y, cp2X, cp2Y, endX, endY];
}
